import type {
    BookingService as B2BookingService,
    BookingServiceEmailReminder,
    BookingQuestionAssignment,
    BookingServiceStaffGroup,
    BookingCustomerInformationRequirements,
    BookingSchedulingPolicy,
    BookingServiceType,
    BookingServiceFormInfo,
    BookingServiceCategory,
    BookingServiceDefaultPriceType,
    BookingUrlCodesEntry,
} from 'owa-graph-schema';
import type BookingService from '../schema/BookingService';
import mapBookingPostalAddress from './mapBookingPostalAddress';
import mapBookingSchedulingPolicy from './mapBookingSchedulingPolicy';
import mapBookingQuestionAssignment from './modelB2/mapBookingQuestionAssignment';
import mapBookingServiceStaffGroup from './modelB2/mapBookingServiceStaffGroup';
import mapBookingServiceType from './modelB2/mapBookingServiceType';
import mapCustomerInformationRequirements from './modelB2/mapCustomerInformationRequirements';
import mapBookingServiceEmailReminder from './modelB2/mapBookingServiceEmailReminder';
import getDurationInMinutes from '../utils/getDurationInMinutes';
import mapStaffMemberIds from './modelB2/mapStaffMemberIds';
import mapBookingServiceDefaultPriceType from './modelB2/mapBookingServiceDefaultPriceType';
import mapBookingServiceFormInfo from './modelB2/mapBookingServiceFormInfo';
import mapBookingServiceCategory from './modelB2/mapBookingServiceCategory';

const mapB2BookingService = (bookingService: B2BookingService, source?: string) => {
    const {
        title,
        description,
        defaultLocation,
        notes,
        serviceType,
        defaultDuration,
        preBuffer,
        postBuffer,
        staffMemberIds,
        customQuestions,
        staffGroups,
        currencyIsoSymbol,
        currencySymbol,
        defaultPriceType,
        bookingsSchedulingPolicy,
        bookingsCustomerInformationRequirements,
        bookingServiceFormInfos,
        emailReminders,
        smsNotificationsEnabled,
        isLocationOnline,
        isWebrtcOnlineMeetingEnabled,
        webUrl,
        defaultPrice,
        serviceId,
        additionalInfo,
        bookingMultiStaffSupportEnabled,
        bookingServiceLanguage,
        bookingServiceCategory,
        isHiddenFromCustomers,
        maximumAttendeesCount,
        canCustomerManageTheirBooking,
        bookingUrlCodes,
        categoryNames,
    } = bookingService as B2BookingService;

    const mappedBookingService: BookingService = {
        DisplayName: title as string,
        Description: description as string,
        DefaultLocation: mapBookingPostalAddress(
            defaultLocation?.address ?? {},
            defaultLocation?.coordinates ?? {}
        ),
        InternalNotes: notes as string,
        BookingServiceType: mapBookingServiceType(serviceType as BookingServiceType),
        DefaultDurationMinutes: getDurationInMinutes(defaultDuration as string),
        PreBuffer: getDurationInMinutes(preBuffer as string),
        PostBuffer: getDurationInMinutes(postBuffer as string),
        StaffPersonIds: mapStaffMemberIds(staffMemberIds as string[]),
        CustomQuestionIds: mapBookingQuestionAssignment(
            customQuestions as BookingQuestionAssignment[]
        ),
        StaffGroups: mapBookingServiceStaffGroup(staffGroups as BookingServiceStaffGroup[]),
        Currency: {
            CurrencyIsoSymbol: currencyIsoSymbol ?? '',
            CurrencySymbol: currencySymbol ?? '',
        },
        PricingType: mapBookingServiceDefaultPriceType(
            defaultPriceType as BookingServiceDefaultPriceType
        ),
        SchedulingPolicy: mapBookingSchedulingPolicy(
            bookingsSchedulingPolicy as BookingSchedulingPolicy,
            source
        ),
        CustomerInformationRequirements: mapCustomerInformationRequirements(
            bookingsCustomerInformationRequirements as BookingCustomerInformationRequirements
        ),
        BookingServiceFormInfoList: mapBookingServiceFormInfo(
            bookingServiceFormInfos as BookingServiceFormInfo[]
        ),
        InboxReminders: mapBookingServiceEmailReminder(
            emailReminders as BookingServiceEmailReminder[]
        ),
        ExcludeFromSelfService: isHiddenFromCustomers as boolean,
        EnableSMSNotifications: smsNotificationsEnabled as boolean,
        IsDefaultLocationOnline: isLocationOnline as boolean,
        IsWebrtcOnlineMeetingEnabled: isWebrtcOnlineMeetingEnabled as boolean,
        WebUrl: webUrl as string,
        Price: defaultPrice as number,
        ServiceId: serviceId as string,
        ItemId: { Id: serviceId as string },
        AdditionalInformation: additionalInfo as string,
        //TODO B2 - Backend has to fix and provide relevant keys
        // Charm?: number,
        Charm: 0, //Using a default value of 0 for now
        BookingCapacityInfo: {
            TotalCapacity: (maximumAttendeesCount ?? 1) as number,
        },
        AddSelfServiceIdForManualBookings: canCustomerManageTheirBooking as boolean,
        BookingMultiStaffSupportEnabled: bookingMultiStaffSupportEnabled as boolean,
        Language: bookingServiceLanguage as string,
        BookingServiceCategoryType: mapBookingServiceCategory(
            bookingServiceCategory as BookingServiceCategory
        ),
        bookingUrlCodes: (bookingUrlCodes ?? []) as BookingUrlCodesEntry[],
        CategoryNames: categoryNames as string[],
    };
    return mappedBookingService as BookingService;
};

export default mapB2BookingService;
